// A filter with a list of refineable values
//   The refine function is passed either from another local component
//     (like when the list is managed by the HierarchicalRefinementList)
//     - import { RefinementList } from "RefinementList"
//   or is passed directly by algolia via connectRefinementList
//     - import RefinementList from "RefinementList"
import React, { useEffect, useState } from "react";
import { connectRefinementList } from "react-instantsearch-dom";
import PropTypes from "prop-types";
import array from "lodash/array";

import promoAttributeReference from "components/search/references/promoAttributeReference";
import FilterPromoContext from "components/search/context/FilterPromoContext";
import RefineElement from "components/search/base/RefineElement";

const propTypes = {
  attribute: PropTypes.string,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }).isRequired
  ),
  currentRefinement: PropTypes.array,
  isAPlus: PropTypes.bool,
  items: PropTypes.array,
  // Note that refinementLimit is specified internally while `limit` is specified by
  // the algolia API, which we are not using here
  refine: PropTypes.func.isRequired,
  refinementLimit: PropTypes.number,

  //
  // Filter promo context props
  //
  setCurrentPromoAttribute: PropTypes.func.isRequired,
  title: PropTypes.string
};

const defaultProps = {
  attribute: null,
  categories: [],
  currentRefinement: [],
  isAPlus: false,
  items: [],
  refinementLimit: -1,
  title: null
};

const Label = ({ label, attribute }) => {
  if (attribute === "literaryPeriod") {
    const [label1, label2] = label.split("(");
    return (
      <>
        {label1}
        <br />
        {`(${label2}`}
      </>
    );
  }

  return label;
};

const isRefineable = (isAPlus, categoryValue) =>
  isAPlus ||
  [
    "!Lit Guide",
    "!Poetry Guide",
    "!Shakescleare",
    "Lit Term > Lit Term"
  ].indexOf(categoryValue) > -1;

const RefinementList = (props) => {
  // Used to show/hide long lists of refinements
  const [showAllRefinements, setShowAllRefinements] = useState(
    props.refinementLimit < 0
  );

  // Show only the number of categories specific by refinementLimit (or all if expanded)
  const [shownCategories, setShownCategories] = useState([]);

  // Refinements can be removed externally, like for instance if disabled when
  // no results are present. This keeps track of any refinements that need removing
  const [refinementsToRemove, setRefinementsToRemove] = useState([]);

  // Keeps track of what elements should be rendered
  const [renderElements, setRenderElements] = useState([]);

  // Updates shown categories when list is expanded/shrunk
  useEffect(() => {
    if (props.refinementLimit < 0 || showAllRefinements) {
      setShownCategories(props.categories);
    } else {
      setShownCategories(props.categories.slice(0, props.refinementLimit));
    }
  }, [showAllRefinements]);

  // Updates the shown list of categories when the list of items changes
  // items include things like if the category is refined and the number
  // of available refinements for that category
  useEffect(() => {
    // If the list of items is empty, it means that we have 0 results from search
    // so we should clear all selected refinements
    if (props.items.length === 0 && props.currentRefinement.length !== 0) {
      setRefinementsToRemove(props.categories.map(({ value }) => value));
    }

    setRenderElements(
      shownCategories.map((category) => {
        let resultCount = 0;
        let isRefined = false;

        if (category.value[0] === "!") {
          const refineableCategories = props.items.filter(
            (i) => i.label.split(" > ")[0] === category.value.slice(1)
          );

          resultCount = refineableCategories.reduce(
            (acc, curr) => acc + curr.count,
            0
          );

          if (
            refineableCategories.length > 0 &&
            refineableCategories.every((i) => i.isRefined)
          ) {
            isRefined = true;
          }
        } else {
          const result = props.items.filter(
            (i) => i.label === category.value
          )[0];
          if (result) {
            resultCount = result.count;
            isRefined = result.isRefined;
          }
        }

        if (isRefined && resultCount === 0 && category.value[0] !== "!") {
          setRefinementsToRemove((prevState) => {
            return array.uniq([...prevState, category.value]);
          });
        }

        const refineable = isRefineable(props.isAPlus, category.value);

        return (
          <RefineElement
            count={resultCount}
            key={category.value}
            Label={<Label label={category.label} attribute={props.attribute} />}
            handleRefine={handleRefine}
            isRefined={isRefined}
            refineable={refineable}
            value={category.value}
          />
        );
      })
    );
  }, [JSON.stringify(props.items), shownCategories.length]);

  // Clears any needed refinements
  useEffect(() => {
    if (refinementsToRemove.length === 0) return;
    if (
      array.intersection(refinementsToRemove, props.currentRefinement)
        .length === 0
    ) {
      setRefinementsToRemove([]);
      return;
    }
    props.refine(
      array.without(props.currentRefinement, ...refinementsToRemove)
    );
    setRefinementsToRemove([]);
  }, [JSON.stringify(refinementsToRemove)]);

  const handleRefine = (value) => {
    if (!isRefineable(props.isAPlus, value)) {
      props.setCurrentPromoAttribute(promoAttributeReference[props.attribute]);
      return;
    }

    // A value starting with ! indicates one of the "All" selections from the
    // flyout, so that's handled differently
    if (value[0] === "!") {
      const allCategories = props.categories
        .filter((ref) => ref.value !== value)
        .map((ref) => ref.value);

      if (allCategories.every((val) => props.currentRefinement.includes(val))) {
        props.refine(array.without(props.currentRefinement, ...allCategories));
      } else {
        // Unique to dedupe categories in case any are already selected when we
        // select all
        props.refine(
          array.uniq([...props.currentRefinement, ...allCategories])
        );
      }
    } else if (props.currentRefinement.indexOf(value) < 0) {
      props.refine([...props.currentRefinement, value]);
    } else {
      props.refine(props.currentRefinement.filter((c) => c !== value));
    }
  };

  const toggleShowAllRefinements = () => {
    setShowAllRefinements((showingRefiments) => !showingRefiments);
  };

  return (
    <div className="refinement-list">
      {props.title && (
        <div className="refinement-list__header">{props.title}</div>
      )}
      <ul className="refinement-list__list">{renderElements}</ul>
      {props.refinementLimit > 0 && (
        <div className="refinement-list__expand-container">
          <button
            className="btn btn-text btn-sm refinement-list__expand"
            onClick={toggleShowAllRefinements}
            type="button"
          >
            {showAllRefinements ? "- Less" : "+ More"}
          </button>
        </div>
      )}
    </div>
  );
};

RefinementList.propTypes = propTypes;
RefinementList.defaultProps = defaultProps;

const RefinementListWithPromoContext = (props) => {
  return (
    <FilterPromoContext.Consumer>
      {(filterPromoContextProps) => (
        <RefinementList {...props} {...filterPromoContextProps} />
      )}
    </FilterPromoContext.Consumer>
  );
};

// Base RefinementList - pass in a refine function
export { RefinementListWithPromoContext as RefinementList };

// Connected RefinementList - algolia passes in the refine function
export default connectRefinementList(RefinementListWithPromoContext);
